import {
    Avatar,
    Box, Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Cards from '../../ReusableComponents/Cards/Cards';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddProduct from './AddProduct';
import SearchBar from '../../ReusableComponents/Search/SearchBar';
import { ButtonStyles } from '../../Styles/ButtonStyles';
import api from '../../api/api';
import CustomSnackbar from '../../components/CustomSnackbar';
import CustomTable from '../../ReusableComponents/Table/CustomTable';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ImageIcon from "@mui/icons-material/Image";
import en_ja from '../../translations/en_ja';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

export default function Products() {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    const [editMode, setEditMode] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [qrImage, setQrImage] = useState("");
    const [qrLoading, setQrLoading] = useState(false);

    const [cardsData, setCardsData] = useState([]);
    const getAllCards = () => {
        api.get("/v1/admin/products/stats")
            .then((res) => {
                setCardsData(res?.data?.data ?? {})
            })
            .catch((err) => console.error(err));
    };

    const handleEdit = (row) => {
        setSelectedProduct(row);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDeleteClick = (row) => {
        setProductToDelete(row);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (productToDelete) {
            try {
                await api.delete(`/v1/admin/delete/product/${productToDelete.id}`);
                setDeleteConfirmOpen(false);
                setProductToDelete(null);
                setSnackbarMessage(en_ja["Product deleted successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
                getAllProducts();
                getAllCards();
            } catch (error) {
                console.error("Error deleting product:", error);
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setProductToDelete(null);
    };

    const formatPrice = (amount) => {
        if (amount === 0) return "¥0";
        return `¥${amount.toLocaleString()}`;
    };

    const handleGenerateQR = async (row) => {
        setQrDialogOpen(true);
        setQrLoading(true);

        try {
            const payload = { id: row.id, type: "product" };
            const res = await api.post("/generate/QR/code", payload);

            if (res?.data?.success) {
                setQrImage(res.data.data.qr_preview);
            }
        } catch (err) {
            console.error("QR Error:", err);
        } finally {
            setQrLoading(false);
        }
    };

    const columns = [
        {
            id: 'title',
            label: en_ja["Product Name"],
            sortable: true,
            accessor: (row) => row.title || "",
            render: (row) => (
                <Box display="flex" alignItems="center" gap={1.5}>
                    {row.primary_image ? (
                        <Avatar src={row.primary_image} alt={row.title} variant="square" sx={{ width: 40, height: 40, borderRadius: 1 }} />
                    ) : (
                        <Avatar variant="square" sx={{ width: 40, height: 40, bgcolor: "grey.300", borderRadius: 1 }}>
                            <ImageIcon fontSize="small" />
                        </Avatar>
                    )}
                    <Typography noWrap>{row.title || en_ja["Untitled"]}</Typography>
                </Box>
            ),
        },
        {
            id: "product_id",
            label: en_ja["Product ID"],
            sortable: true,
        },
        {
            id: "product_code",
            label: en_ja["Product Code"],
            sortable: true,
        },
        {
            id: 'product_price',
            label: en_ja['Price'],
            sortable: true,
            render: (row) => (
                <Typography fontSize="14px" fontWeight="600" color="#28a745">
                    {formatPrice(row.product_price)}
                </Typography>
            )
        },
        {
            id: "actions",
            label: en_ja["Actions"],
            render: (row) => (
                <Box display="flex" justifyContent="start" gap={1}>

                    {/* Edit */}
                    <Tooltip title={en_ja["Edit Product"]} arrow>
                        <IconButton size="small" onClick={() => handleEdit(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* Delete */}
                    <Tooltip title={en_ja["Delete Product"]} arrow>
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            sx={{
                                color: "error.main",
                                "&:hover": { backgroundColor: "error.light", color: "error.dark" },
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* QR Code */}
                    <Tooltip title={en_ja["Generate QR Code"]} arrow>
                        <IconButton
                            size="small"
                            onClick={() => handleGenerateQR(row)}
                            sx={{
                                color: "#1976d2",
                                "&:hover": { backgroundColor: "#e2ecf7" },
                            }}
                        >
                            <QrCode2Icon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                </Box>
            ),
        }
    ];

    const handleUploadProduct = () => {
        setSelectedProduct(null);
        setEditMode(false);
        setDialogOpen(true);
    };

    const handleDialog = () => {
        setDialogOpen(false);
    };

    useEffect(() => {
        getAllProducts();
        getAllCards();
    }, []);

    const getAllProducts = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/v1/admin/get/AllProducts`);
            if (res) {
                const finalData = (res?.data ?? []).map((item) => ({
                    ...item,
                    id: item.product_id,
                }));
                setProductData(finalData);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDialogSubmit = () => {
        getAllProducts();
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
        setSnackbarType("success");
    };

    const filteredData = productData.filter((item) => {
        if (!searchTerm) return true;
        const lower = searchTerm.toLowerCase();
        return (
            (item?.title || "").toLowerCase().includes(lower) ||
            (item?.product_code || "").toLowerCase().includes(lower) ||
            String(item?.product_id).toLowerCase().includes(lower) ||
            String(item?.product_price).toLowerCase().includes(lower)
        );
    });

    return (
        <Box sx={{ mt: 2 }}>

            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={handleSnackbarClose}
            />

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: 3,
                }}
            >
                <Cards
                    title={en_ja["Total Products"]}
                    value={cardsData?.total_products ?? 0}
                    icon={<Inventory2Icon sx={{ color: "white", fontSize: 24 }} />}
                />

                <Cards
                    title={en_ja["Uploaded This Month"]}
                    value={cardsData?.products_uploaded_this_month ?? 0}
                    icon={<CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />}
                />

                <Cards
                    title={en_ja["This Month's Revenue"]}
                    value={cardsData?.Revenue ?? 0}
                    prefix="¥"
                    icon={<AttachMoneyIcon sx={{ color: "white", fontSize: 24 }} />}
                />
            </Box>

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                my: 3
            }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <SearchBar
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Box>

                <Button sx={ButtonStyles} onClick={handleUploadProduct}>
                    {en_ja["Upload Product"]}
                </Button>
            </Box>

            <CustomTable
                columns={columns}
                rows={filteredData}
                searchTerm={searchTerm}
                loading={loading}
            />

            <AddProduct
                dialogOpen={dialogOpen}
                onClose={handleDialog}
                onSubmit={handleDialogSubmit}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
                productData={selectedProduct}
                editMode={editMode}
            />

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
                    {en_ja["Confirm Delete"]}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {en_ja["Are you sure you want to delete the product"]} <strong>{productToDelete?.title}</strong>? {en_ja["This action cannot be undone."]}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleDeleteCancel} variant="outlined" color="primary">
                        {en_ja["Cancel"]}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        autoFocus
                    >
                        {en_ja["Delete"]}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={qrDialogOpen}
                onClose={() => setQrDialogOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: 300,
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 1
                    }}
                >

                    {en_ja["QR Code"]}
                    <IconButton onClick={() => setQrDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* QR Content */}
                <DialogContent
                    sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: 220,
                            height: 220,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                        }}
                    >
                        {qrLoading ? (
                            <CircularProgress />
                        ) : (
                            <img
                                src={qrImage}
                                alt="QR Code"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        )}
                    </Box>
                </DialogContent>

            </Dialog>
        </Box>
    );
}
